/**
 * @module pages/MitarbeiterPulsePage
 * @description Team-Pulse-Uebersicht: KPI-Bar, Ranking-Tabelle, Score-Verteilung.
 * Zeigt Performance-Scores aller Mitarbeiter mit SOLL/IST-Vergleich.
 * @since v2.161.0
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Activity, ArrowUpRight, ArrowDownRight, Minus, FileText, ChevronUp, ChevronDown, Loader2, CheckCircle2, XCircle, Gauge, Trophy, AlertTriangle, Target } from 'lucide-react';
import MitarbeiterListItem from '../components/MitarbeiterListItem';

const API = import.meta.env.VITE_API_URL || '';

function rd1(n) { return n != null ? Number(n).toFixed(1).replace('.', ',') : '–'; }

export default function MitarbeiterPulsePage() {
  const { t } = useLanguage();
  const [zeitraum, setZeitraum] = useState('monat');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState('gesamt');
  const [sortDir, setSortDir] = useState('desc');
  const [generatingId, setGeneratingId] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', text }
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/mitarbeiter/pulse/ranking?zeitraum=${zeitraum}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [zeitraum]);

  const sorted = useMemo(() => {
    if (!data?.mitarbeiter) return [];
    const list = [...data.mitarbeiter];
    list.sort((a, b) => {
      let va, vb;
      if (sortCol === 'name') {
        va = a.name; vb = b.name;
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortCol === 'pakete_pro_tag') {
        va = a.pakete_pro_tag || 0; vb = b.pakete_pro_tag || 0;
      } else if (sortCol === 'soll_ist_ratio') {
        va = a.soll_ist_ratio || 0; vb = b.soll_ist_ratio || 0;
      } else {
        va = a.scores?.[sortCol] ?? a.scores?.gesamt ?? 0;
        vb = b.scores?.[sortCol] ?? b.scores?.gesamt ?? 0;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, sortCol, sortDir]);

  const teamAvg = data?.team_avg || {};
  const topPerformer = sorted[0];
  const needsImprovement = sorted.filter(m => (m.scores?.gesamt || 0) < 40).length;
  const avgSollIst = useMemo(() => {
    if (!sorted.length) return null;
    const vals = sorted.filter(m => m.soll_ist_ratio != null).map(m => m.soll_ist_ratio);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length) : null;
  }, [sorted]);

  // Score distribution for bar chart
  const distribution = useMemo(() => {
    const buckets = [
      { range: '0–20', count: 0 }, { range: '20–40', count: 0 },
      { range: '40–60', count: 0 }, { range: '60–80', count: 0 }, { range: '80–100', count: 0 },
    ];
    for (const m of (data?.mitarbeiter || [])) {
      const s = m.scores?.gesamt || 0;
      if (s < 20) buckets[0].count++;
      else if (s < 40) buckets[1].count++;
      else if (s < 60) buckets[2].count++;
      else if (s < 80) buckets[3].count++;
      else buckets[4].count++;
    }
    return buckets;
  }, [data]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  async function handleGenerateReport(maId) {
    setGeneratingId(maId);
    setToast(null);
    try {
      const res = await fetch(`${API}/api/mitarbeiter/${maId}/pulse/report`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zeitraum }),
      });
      const result = await res.json();
      if (!res.ok || result.error) {
        setToast({ type: 'error', text: result.error || t('maPulse.reportError') });
        setGeneratingId(null);
        return;
      }
      setToast({ type: 'success', text: t('maPulse.reportSuccess') });
      setGeneratingId(null);
      setTimeout(() => navigate(`/mitarbeiter/${maId}/pulse`), 800);
    } catch {
      setToast({ type: 'error', text: t('maPulse.reportError') });
      setGeneratingId(null);
    }
  }

  function SortHeader({ col, label, className = '' }) {
    const active = sortCol === col;
    return (
      <th
        className={`px-3 py-2.5 text-left cursor-pointer select-none hover:text-[var(--text-primary)] transition-colors ${className}`}
        onClick={() => toggleSort(col)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </span>
      </th>
    );
  }

  function ScoreBar({ value, maxVal = 100 }) {
    const pct = Math.min(100, Math.max(0, (value / maxVal) * 100));
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-2 rounded-full" style={{ background: 'var(--border-card, #E5E7EB)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#338edc' }} />
        </div>
        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{value}</span>
      </div>
    );
  }

  function TrendIcon({ trend }) {
    if (trend === 'steigend') return <ArrowUpRight size={14} style={{ color: '#10B981' }} />;
    if (trend === 'fallend') return <ArrowDownRight size={14} style={{ color: '#EF4444' }} />;
    return <Minus size={14} style={{ color: 'var(--text-tertiary)' }} />;
  }

  const zeitraumOptions = [
    { key: 'woche', label: t('maPulse.zeitraum.woche') },
    { key: 'monat', label: t('maPulse.zeitraum.monat') },
    { key: 'quartal', label: t('maPulse.zeitraum.quartal') },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity size={24} style={{ color: 'var(--text-secondary)' }} />
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {t('maPulse.title')}
          </h1>
        </div>
        {/* Zeitraum Toggle */}
        <div className="flex rounded-[10px] p-[3px] border" style={{ background: 'var(--bg-inset, #F8F9FC)', borderColor: 'var(--border-card, #E5E7EB)' }}>
          {zeitraumOptions.map(o => (
            <button
              key={o.key}
              onClick={() => setZeitraum(o.key)}
              className={`px-4 py-1.5 text-sm rounded-[8px] transition-all ${
                zeitraum === o.key
                  ? 'bg-white dark:bg-[var(--bg-card)] font-semibold shadow-sm'
                  : 'font-normal'
              }`}
              style={{ color: zeitraum === o.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t('maPulse.teamAvg')} value={teamAvg.gesamt ?? '–'} sub={`${sorted.length} ${t('maPulse.mitarbeiter')}`} icon={Gauge} iconColor="text-[#8B5CF6]" />
        <KpiCard label={t('maPulse.topPerformer')} value={topPerformer?.name?.split(' ')[0] || '–'} sub={topPerformer ? `Score: ${topPerformer.scores?.gesamt}` : ''} icon={Trophy} iconColor="text-[#F59E0B]" />
        <KpiCard label={t('maPulse.verbesserung')} value={needsImprovement} sub={`Score < 40`} icon={AlertTriangle} iconColor="text-[#EF4444]" />
        <KpiCard label={t('maPulse.sollIst')} value={avgSollIst != null ? rd1(avgSollIst) : '–'} sub={t('maPulse.sollIstTeamAvg')} icon={Target} iconColor="text-[#338edc]" />
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-12 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
          {t('maPulse.loading')}
        </div>
      )}

      {/* Ranking Table */}
      {!loading && sorted.length > 0 && (
        <div className="border rounded-2xl overflow-hidden" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-table, #F3F4F6)' }}>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>#</th>
                  <SortHeader col="name" label={t('maPulse.name')} className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }} />
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.rolle')}</th>
                  <SortHeader col="gesamt" label={t('maPulse.scores.gesamt')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <SortHeader col="geschwindigkeit" label={t('maPulse.scores.geschwindigkeit')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <SortHeader col="effizienz" label={t('maPulse.scores.effizienz')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <SortHeader col="volumen" label={t('maPulse.scores.volumen')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <SortHeader col="konsistenz" label={t('maPulse.scores.konsistenz')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.trend.label')}</th>
                  <SortHeader col="pakete_pro_tag" label={t('maPulse.paketeProTag')} className="text-[11px] font-semibold uppercase tracking-wider" />
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m, idx) => (
                  <tr
                    key={m.id}
                    className="border-b last:border-0 transition-colors"
                    style={{ borderColor: 'var(--border-table, #F3F4F6)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover, #FAFAFE)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td className="px-3 py-3 text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>{idx + 1}</td>
                    <td className="px-3 py-3">
                      <Link to={`/mitarbeiter/${m.id}/pulse`} className="block hover:underline">
                        <MitarbeiterListItem mitarbeiter={m} size="sm" apiBase={API} />
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {m.rolle === 'picker' ? 'Picker' : m.rolle === 'packer' ? 'Packer' : 'Pick & Pack'}
                    </td>
                    <td className="px-3 py-3"><ScoreBar value={m.scores?.gesamt || 0} /></td>
                    <td className="px-3 py-3"><ScoreBar value={m.scores?.geschwindigkeit || 0} /></td>
                    <td className="px-3 py-3"><ScoreBar value={m.scores?.effizienz || 0} /></td>
                    <td className="px-3 py-3"><ScoreBar value={m.scores?.volumen || 0} /></td>
                    <td className="px-3 py-3"><ScoreBar value={m.scores?.konsistenz || 0} /></td>
                    <td className="px-3 py-3"><TrendIcon trend={m.trend} /></td>
                    <td className="px-3 py-3 text-sm" style={{ color: 'var(--text-primary)' }}>{rd1(m.pakete_pro_tag)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => handleGenerateReport(m.id)}
                        disabled={!!generatingId}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors hover:bg-[var(--bg-inset)] disabled:opacity-50"
                        style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary)' }}
                        title={t('maPulse.generateReport')}
                      >
                        {generatingId === m.id
                          ? <Loader2 size={13} className="animate-spin" />
                          : <FileText size={13} />}
                        {generatingId === m.id ? t('maPulse.generating') : 'Report'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !sorted.length && (
        <div className="border rounded-2xl p-12 text-center" style={{ borderColor: 'var(--border-card)', background: 'var(--bg-card)' }}>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.noData')}</p>
        </div>
      )}

      {/* Score Distribution Chart */}
      {!loading && distribution.some(b => b.count > 0) && (
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.scoreVerteilung')}
          </h3>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distribution} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-table, #F3F4F6)" />
                <XAxis
                  dataKey="range"
                  tick={{ fontSize: 12, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card, #fff)',
                    border: '1px solid var(--border-card, #E5E7EB)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  formatter={(val) => [val, t('maPulse.mitarbeiter')]}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {distribution.map((_, i) => (
                    <Cell key={i} fill="#22D3EE" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className="flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium shadow-sm"
            style={{
              background: 'var(--bg-card, #fff)',
              borderColor: toast.type === 'success' ? '#10B981' : '#EF4444',
              color: 'var(--text-primary)',
            }}
          >
            {toast.type === 'success'
              ? <CheckCircle2 size={16} style={{ color: '#10B981' }} />
              : <XCircle size={16} style={{ color: '#EF4444' }} />}
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
}

/** KPI Card */
function KpiCard({ label, value, sub, icon: Icon, iconColor }) {
  return (
    <div
      className="border rounded-2xl p-5"
      style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className={iconColor || 'text-[#8B5CF6]'} />}
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </div>
      </div>
      <div className="text-[28px] font-bold leading-none mb-1" style={{ color: 'var(--text-primary)' }}>
        {value}
      </div>
      {sub && (
        <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{sub}</div>
      )}
    </div>
  );
}
